import { Suspense, useEffect } from 'react';
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { CircularProgress, ThemeProvider } from '@gapartners/ui';
import { useSelector } from 'react-redux';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { PublicRoute } from './components/auth/PublicRoute';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { AmazonConnectCCPProvider } from '@/components/amazon-connect/AmazonConnectCCP';
import { AmazonConnectAgentProvider } from '@/components/amazon-connect/AmazonConnectAgentContext';
import { connectAdapter } from '@/connect/adapter/streamsConnectAdapter';
import { AmazonConnectContactsProvider } from '@/hooks/useAmazonConnectContacts';
import { TransferRequestInbox } from '@/components/contact-center/TransferRequestInbox';
import { LiveAlertsPopupStack } from '@/components/manager/LiveAlertsPopup';
import { alertService } from '@/services/alertService';
import { routes } from './config/routes';
import { useMultiTabDetection } from '@/hooks/useMultiTabDetection';
import { MultiTabWarning } from '@/components/common/MultiTabWarning';
import { DesktopNotificationPrompt } from '@/components/common/DesktopNotificationPrompt';
import { ALERTS_NAVIGATION_EVENT } from '@/utils/desktopNotification';
import { ROUTES } from '@/constants';
import type { ConnectContact } from '@/types';
import type { RootState } from '@/store';

function App() {
  const { isBlocked } = useMultiTabDetection();
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  );
  const isManager = useSelector(
    (state: RootState) => !!state.auth.user?.isManager
  );

  useEffect(() => {
    if (!isAuthenticated) return;
    const setOffline = () => connectAdapter.setAgentOffline();
    window.addEventListener('beforeunload', setOffline);
    window.addEventListener('pagehide', setOffline);
    return () => {
      window.removeEventListener('beforeunload', setOffline);
      window.removeEventListener('pagehide', setOffline);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !isManager) return;
    if (!alertService.isListening()) {
      alertService.startListening();
    }
    return () => {
      alertService.stopListening();
    };
  }, [isAuthenticated, isManager]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const handleNavigateToAlerts = () =>
      navigate(`${ROUTES.MANAGER.ROOT}/${ROUTES.MANAGER.ALERTS}`);
    window.addEventListener(ALERTS_NAVIGATION_EVENT, handleNavigateToAlerts);
    return () =>
      window.removeEventListener(
        ALERTS_NAVIGATION_EVENT,
        handleNavigateToAlerts
      );
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    // Guard: CCP not yet initialized (connectAdapter will throw if window.connect is absent).
    try {
      connectAdapter.getCcpEnums();
    } catch {
      return;
    }

    const redirectToDashboardIfNeeded = (contact: ConnectContact) => {
      let contactId: string | undefined;
      try {
        contactId = contact.getContactId();
      } catch {
        contactId = undefined;
      }
      const expectedMonitor = connectAdapter.isMonitorContact(contact);
      const inbound = contact.isInbound();
      console.log('[Connect][ExpectedMonitor][RedirectCheck]', {
        contactId,
        expectedMonitor,
        inbound,
        pathname: location.pathname,
      });
      if (expectedMonitor) return;
      if (!inbound) return;
      if (location.pathname === ROUTES.DASHBOARD) return;
      console.log(
        '[Connect][ExpectedMonitor][RedirectCheck] navigating to dashboard',
        {
          contactId,
        }
      );
      navigate(ROUTES.DASHBOARD);
    };

    let unsubscribe: unknown;
    try {
      unsubscribe = connectAdapter.onContact(rawContact => {
        const contact = rawContact as ConnectContact;
        contact.onIncoming(() => redirectToDashboardIfNeeded(contact));
        contact.onConnecting(() => redirectToDashboardIfNeeded(contact));
        contact.onPending(() => redirectToDashboardIfNeeded(contact));
      });
    } catch {
      return;
    }

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [isAuthenticated, location.pathname, navigate]);

  if (isBlocked) {
    return <MultiTabWarning />;
  }

  const routesContent = (
    <Routes>
      {routes.map(route => (
        <Route
          key={route.path}
          path={route.path}
          element={
            route.isProtected ? (
              <ProtectedRoute>{route.element}</ProtectedRoute>
            ) : (
              <PublicRoute>{route.element}</PublicRoute>
            )
          }
        >
          {route.children?.map(child => (
            <Route
              key={child.path ?? 'index'}
              path={child.path}
              index={child.index}
              element={child.element}
            />
          ))}
        </Route>
      ))}

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );

  const content = isAuthenticated ? (
    <AmazonConnectCCPProvider>
      <AmazonConnectAgentProvider>
        <AmazonConnectContactsProvider>
          <>
            {routesContent}
            <TransferRequestInbox />
            <LiveAlertsPopupStack />
            <DesktopNotificationPrompt />
          </>
        </AmazonConnectContactsProvider>
      </AmazonConnectAgentProvider>
    </AmazonConnectCCPProvider>
  ) : (
    routesContent
  );

  return (
    <ErrorBoundary>
      <Suspense fallback={<CircularProgress />}>
        <ThemeProvider>{content}</ThemeProvider>
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
